/** `resolveRedirectUri` の解決結果。 */
export interface ResolvedRedirect {
  /**
   * 遷移先。
   * 同一オリジンはアプリ内パス（`/foo?bar#baz`）、別オリジンは絶対URLに正規化される。
   */
  href: string
  /**
   * 別オリジンかどうか。
   * true の場合は next/router ではなく `window.location` で遷移すること
   * （next/router は外部URLだと内部で `window.location.href = url` になり push 相当になる）。
   */
  isExternal: boolean
}

/**
 * `redirect_uri` クエリパラメータから遷移先を解決する。
 *
 * 同一オリジン・別オリジンのいずれも許可する。ただしスキームは `http` / `https` のみとし、
 * `javascript:` / `data:` 等の実行可能スキームは XSS になるため拒否する。
 * 解決できない値は null を返すので、呼び出し側は既定の戻り先へフォールバックすること。
 *
 * ⚠️ 別オリジンを許可しているため、この値は任意のサイトへの遷移を引き起こす
 * （＝オープンリダイレクト）。遷移先を絞りたい場合は許可オリジンのリストで
 * `isExternal` の結果をさらにフィルタすること。
 *
 * origin を引数で受け取るのは、SSR / 静的エクスポート時に `window` が無いケースと
 * ユニットテスト（node 環境）の双方を扱えるようにするため。
 *
 * @param value router.query.redirect_uri の値
 * @param origin 現在のオリジン（例: `window.location.origin`）。相対パスの解決基準になる
 *
 * @example
 * // ('/sp2026/spots/detail?id=1', 'https://app.raund.net')
 * //   → { href: '/sp2026/spots/detail?id=1', isExternal: false }
 * // ('https://app.raund.net/sp2026/', 'https://app.raund.net')
 * //   → { href: '/sp2026/', isExternal: false }
 * // ('https://example.com/partner', 'https://app.raund.net')
 * //   → { href: 'https://example.com/partner', isExternal: true }
 * // ('javascript:alert(1)', 'https://app.raund.net') → null
 */
export function resolveRedirectUri(value: string | string[] | undefined, origin: string): ResolvedRedirect | null {
  if (typeof value !== 'string' || value === '') return null

  try {
    const base = new URL(origin)
    const resolved = new URL(value, base)

    // http / https 以外（javascript:, data:, file: 等）は遷移先として認めない
    if (resolved.protocol !== 'https:' && resolved.protocol !== 'http:') return null

    // 同一オリジンはアプリ内パスへ正規化し、next/router のクライアント遷移に載せられるようにする
    if (resolved.origin === base.origin) {
      return { href: `${resolved.pathname}${resolved.search}${resolved.hash}`, isExternal: false }
    }
    return { href: resolved.toString(), isExternal: true }
  } catch {
    // origin が空文字（SSR）や value が URL として不正な場合
    return null
  }
}
